<template>
  <div>
    <h5 id="dates">
      <a href="#dates" class="text-body">
        {{ $t('Country and Time Zone') }}
      </a>
    </h5>
    <div class="form-group">
      <label for="tour-country">
        {{ $t("Country") }}
      </label>
      <Combobox
        id="tour-country"
        name="country"
        v-model="selectedCountry"
        :options="countryOptions.map(country => ({ value: country.id, label: country.text }))"
        :fullwidth="true"
        required
        />
    </div>

    <div class="form-group">
      <label for="tour-timezone">
        {{ $t("Timezone") }}
      </label>
      <timezone
                id="tour-timezone"
                name="timezone"
                :value="zone"
                :timezones="timezones"
                @timezone-change="onTimezoneChange"
                required>
      </timezone>
    </div>

    <h5 id="dates">
      <a href="#dates" class="text-body">
        {{ $t('Event Dates') }}
      </a>
    </h5>

    <div class="tw:bg-zinc-50 tw:border tw:p-4 tw:rounded-md tw:mb-3">
      <div class="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <label for="has-no-dates">
          <span>
            {{ $t("This event is permanent") }}
          </span>
          <small class="text-muted d-block">{{ $t('If not please specify the dates') }}</small>
        </label>
        <div>
          <Toggle id="has-no-dates" v-model="hasNoDates"/>
        </div>
      </div>

      <div v-if="!hasNoDates" class="tw:mt-3 tw:pt-3 tw:border-t">
        <div class="form-group">
          <label for="frmTourStartDate">{{ $t('Start at:') }}</label>
          <Datetime id="frmTourStartDate"
                    v-model="start"
                    :timezone="zone"
                    :required="!hasNoDates"
                    :disabled="!zone"
                    with-timezone
          ></Datetime>
          <input type="hidden" name="start_at" :value="formatedStartAt">
        </div>

        <div class="form-group">
          <label for="frmTourEndDate">{{ $t('End at:') }}</label>
          <Datetime id="frmTourEndDate"
                    v-model="end"
                    :timezone="zone"
                    :required="!hasNoDates"
                    :disabled="!zone"
                    with-timezone
          ></Datetime>
          <input type="hidden" name="end_at" :value="formatedEndAt">
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import Timezone from '@/components/form-components/Timezone.vue';
import Datetime from '@/components/ui/Datetime.vue';
import Toggle from '@/components/ui/Toggle.vue'
import ct from 'countries-and-timezones';
import Combobox from "@/components/ui/Combobox.vue";

const props = defineProps<{
  timezones: Array<string>,
  timezone?: string,
  startDate?: string | null,
  endDate?: string | null,
  countryList: Record<string, { value: string, label: string }>,
  country?: string
}>();

const zone = ref<string | null>(props.timezone ?? null);
const start = ref<Date | null>(props.startDate ? new Date(Date.parse(props.startDate)) : null);
const end = ref<Date | null>(props.endDate ? new Date(Date.parse(props.endDate)) : null);
const hasNoDates = ref<boolean>(true);
const selectedCountry = ref<string | null>(props.country ?? null);

const dateFormater = (dateToFormat: string | null | Date) => {
  if (dateToFormat instanceof Date) {
      return dateToFormat.toISOString();
    } else if (typeof dateToFormat === "string") {
      return dateToFormat;
    }
  return null;
}

const formatedStartAt = computed(() => {
  return dateFormater(start.value);
})

const formatedEndAt = computed(() => {
  return dateFormater(end.value);
})

const countryOptions = computed(() => {
  return Object.values(props.countryList).map(country => ({
    id: country.value,
    text: country.label
  }));
});

const onTimezoneChange = (value: string) => {
  zone.value = value;
};

watch(selectedCountry, (newCountry) => {
  if (!newCountry || zone.value) return;
  const countryData = ct.getCountry(newCountry);
  if (!countryData) return;
  zone.value = countryData.timezones[0];
});

onMounted(() => {
  if (start.value || end.value) {
    hasNoDates.value = false;
  }
});
</script>
